import React, { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Copy, Target, Plus, Upload, X } from "lucide-react";
import { toast } from "sonner";

export default function AdKitRepository() {
    const { user } = useAuth();
    const [kits, setKits] = useState([]);
    const [filter, setFilter] = useState("all");
    const [showCreate, setShowCreate] = useState(false);
    const fileRef = useRef(null);
    const [creating, setCreating] = useState({
        state_code: "",
        sector: "",
        title: "",
        hook: "",
        targeting: "",
        cta: "",
        thumbnail: "",
    });
    const [uploading, setUploading] = useState(false);

    const load = () => api.get("/ad-kits").then(({ data }) => setKits(data));
    useEffect(() => { load(); }, []);

    const states = Array.from(new Set(kits.map((k) => k.state_code))).sort();
    const filtered = filter === "all" ? kits : kits.filter((k) => k.state_code === filter);
    const territories = user?.territories || [];

    const copy = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard.");
    };

    const onFile = async (file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        setUploading(true);
        try {
            const { data } = await api.post("/uploads/ad-kit", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setCreating((c) => ({ ...c, thumbnail: data.url }));
            toast.success("Image uploaded.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const submitCreate = async (e) => {
        e.preventDefault();
        try {
            await api.post("/ad-kits", creating);
            toast.success("Ad kit deployed.");
            setShowCreate(false);
            setCreating({ state_code: "", sector: "", title: "", hook: "", targeting: "", cta: "", thumbnail: "" });
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to create");
        }
    };

    return (
        <div className="space-y-8">
            <div className="fade-up flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.35em] text-muted-foreground">High-Fidelity Ad Kit</div>
                    <h1 className="font-heading text-4xl sm:text-5xl mt-1 tracking-tighter">Creative Repository</h1>
                    <p className="text-muted-foreground mt-2 max-w-2xl">
                        Localised hooks, targeting parameters, and creative thumbnails for your
                        claimed territories. Copy, remix, deploy.
                    </p>
                </div>
                {territories.length > 0 && (
                    <button
                        onClick={() => setShowCreate(true)}
                        data-testid="adkit-new"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors"
                    >
                        <Plus size={16} /> New Ad Kit
                    </button>
                )}
            </div>

            {showCreate && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
                    <form
                        onSubmit={submitCreate}
                        onClick={(e) => e.stopPropagation()}
                        className="glass rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
                        data-testid="adkit-create-form"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div className="font-heading text-2xl">New ad kit</div>
                            <button type="button" onClick={() => setShowCreate(false)} className="text-muted-foreground hover:text-foreground" data-testid="adkit-close">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <select required value={creating.state_code} onChange={(e) => setCreating({ ...creating, state_code: e.target.value })} className="px-3 py-2.5 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] font-mono text-sm" data-testid="adkit-state">
                                    <option value="">State</option>
                                    {territories.map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                                <input required placeholder="Sector" value={creating.sector} onChange={(e) => setCreating({ ...creating, sector: e.target.value.toLowerCase() })} className="px-3 py-2.5 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] text-sm" data-testid="adkit-sector" />
                            </div>
                            <input required placeholder="Title" value={creating.title} onChange={(e) => setCreating({ ...creating, title: e.target.value })} className="w-full px-3 py-2.5 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] text-sm" data-testid="adkit-title" />
                            <textarea required placeholder="Hook (the opening line)" rows={2} value={creating.hook} onChange={(e) => setCreating({ ...creating, hook: e.target.value })} className="w-full px-3 py-2.5 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] text-sm resize-none" data-testid="adkit-hook" />
                            <textarea required placeholder="Targeting parameters" rows={2} value={creating.targeting} onChange={(e) => setCreating({ ...creating, targeting: e.target.value })} className="w-full px-3 py-2.5 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] text-sm resize-none" data-testid="adkit-targeting" />
                            <input required placeholder="Call to action" value={creating.cta} onChange={(e) => setCreating({ ...creating, cta: e.target.value })} className="w-full px-3 py-2.5 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] text-sm" data-testid="adkit-cta" />

                            <div>
                                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="adkit-file-input" />
                                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-md border border-dashed border-[hsl(40_40%_30%/0.4)] text-sm hover:bg-[hsl(151_55%_15%)] transition-colors disabled:opacity-50" data-testid="adkit-upload-btn">
                                    <Upload size={14} /> {uploading ? "Uploading..." : creating.thumbnail ? "Replace image" : "Upload thumbnail"}
                                </button>
                                {creating.thumbnail && (
                                    <img src={creating.thumbnail} alt="preview" className="mt-2 w-full h-32 object-cover rounded-md" />
                                )}
                            </div>
                        </div>
                        <button type="submit" className="w-full mt-5 py-3 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors" data-testid="adkit-create-submit">
                            Deploy Ad Kit
                        </button>
                    </form>
                </div>
            )}

            {kits.length === 0 ? (
                <div className="glass rounded-2xl p-10 text-center text-muted-foreground">
                    Claim a territory first to unlock localised ad kits.
                </div>
            ) : (
                <>
                    {/* State filter */}
                    <div className="flex flex-wrap gap-2">
                        <FilterPill active={filter === "all"} onClick={() => setFilter("all")} label="All" />
                        {states.map((s) => (
                            <FilterPill key={s} active={filter === s} onClick={() => setFilter(s)} label={s} />
                        ))}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                        {filtered.map((k) => (
                            <div key={k.id} className="glass rounded-2xl overflow-hidden group hover:-translate-y-1 transition-transform" data-testid={`adkit-${k.id}`}>
                                <div
                                    className="h-40 bg-cover bg-center relative"
                                    style={{ backgroundImage: `url(${k.thumbnail})` }}
                                >
                                    <div className="absolute inset-0 bg-gradient-to-t from-[hsl(151_55%_8%)] via-transparent" />
                                    <div className="absolute top-3 left-3 flex items-center gap-1">
                                        <span className="text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold">{k.state_code}</span>
                                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[hsl(151_55%_8%/0.8)] text-foreground capitalize">{k.sector}</span>
                                    </div>
                                </div>
                                <div className="p-5">
                                    <div className="font-heading text-lg leading-snug">{k.title}</div>
                                    <p className="mt-2 text-sm italic text-[hsl(45_70%_75%)]">"{k.hook}"</p>

                                    <div className="mt-4 space-y-2 text-xs">
                                        <div className="flex items-start gap-2 text-muted-foreground">
                                            <Target size={12} className="mt-0.5 shrink-0" />
                                            <span>{k.targeting}</span>
                                        </div>
                                        <div className="inline-flex items-center gap-2 px-2 py-1 rounded-md bg-[hsl(151_55%_13%)] text-[hsl(45_85%_68%)] font-mono text-[11px]">
                                            CTA: {k.cta}
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => copy(`${k.hook}\n\n${k.cta}`)}
                                        data-testid={`copy-hook-${k.id}`}
                                        className="mt-4 w-full text-xs py-2 rounded-md border border-[hsl(40_50%_35%/0.35)] hover:bg-[hsl(151_55%_15%)] transition-colors inline-flex items-center justify-center gap-1.5"
                                    >
                                        <Copy size={12} /> Copy hook
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function FilterPill({ active, onClick, label }) {
    return (
        <button
            onClick={onClick}
            data-testid={`adkit-filter-${label.toLowerCase()}`}
            className={`text-xs px-3 py-1.5 rounded-full transition-colors ${active ? "bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold" : "bg-[hsl(151_55%_13%)] text-muted-foreground hover:text-foreground"}`}
        >
            {label}
        </button>
    );
}
